import React, { useState, useEffect } from 'react';
import {
    Search,
    Users,
    Building2,
    ChevronDown,
    ChevronUp,
    AlertCircle,
    Settings
} from 'lucide-react';
import { TeamDetail } from '../../../types/settings/TeamTypes';
import TeamMembersModal from '../../../components/modals/TeamMembersModal';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface ModernTeamsTableProps {
    initialData: TeamDetail[];
    onEdit: (team: TeamDetail) => void;
    pagination?: PaginationProps;
    onPageChange?: (page: number) => void;
    onSearch?: (term: string) => Promise<void>;
    onSort?: (column: string, order: 'ASC' | 'DESC') => Promise<void>;
    onRefresh?: () => void;
    isLoading?: boolean;
    searchTerm?: string;
    sortColumn?: string;
    sortOrder?: 'ASC' | 'DESC';
}

const ModernTeamsTable: React.FC<ModernTeamsTableProps> = ({
    initialData,
    onEdit,
    pagination,
    onPageChange,
    onSearch,
    onSort,
    onRefresh,
    isLoading = false,
    searchTerm: initialSearchTerm = '',
    sortColumn = '',
    sortOrder = 'ASC'
}) => {
    const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
    const [managingTeam, setManagingTeam] = useState<TeamDetail | null>(null);

    useEffect(() => {
        setSearchTerm(initialSearchTerm);
    }, [initialSearchTerm]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        onSearch?.(searchTerm);
    };


    const handleSort = (column: string) => {
        const newOrder = sortColumn === column && sortOrder === 'ASC' ? 'DESC' : 'ASC';
        onSort?.(column, newOrder);
    };

    const columns = [
        { key: 'team_name', label: 'Team Name', sortable: true },
        { key: 'team_members', label: 'Members', sortable: false }
    ];

    return (
        <>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            {/* Search Bar */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <form onSubmit={handleSearch} className="flex gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search teams by name..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Search
                    </button>
                </form>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                        <tr>
                            {columns.map(column => (
                                <th
                                    key={column.key}
                                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
                                        column.sortable ? 'cursor-pointer hover:text-gray-700 dark:hover:text-gray-200' : ''
                                    }`}
                                    onClick={() => column.sortable && handleSort(column.key)}
                                >
                                    <div className="flex items-center gap-1">
                                        {column.label}
                                        {column.sortable && sortColumn === column.key && (
                                            sortOrder === 'ASC' ?
                                                <ChevronUp className="w-4 h-4" /> :
                                                <ChevronDown className="w-4 h-4" />
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {isLoading ? (
                            <tr>
                                <td colSpan={columns.length} className="px-6 py-8 text-center">
                                    <div className="flex justify-center items-center space-x-2">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
                                        <span className="text-gray-500 dark:text-gray-400">Loading teams...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : initialData.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="px-6 py-8 text-center">
                                    <div className="flex flex-col items-center justify-center">
                                        <AlertCircle className="w-12 h-12 text-gray-400 mb-3" />
                                        <p className="text-gray-500 dark:text-gray-400">No teams found</p>
                                        {searchTerm && (
                                            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                                                Try adjusting your search criteria
                                            </p>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            initialData.map((team) => (
                                <React.Fragment key={team.uuid}>
                                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 bg-brand-100 dark:bg-brand-900/20 rounded-lg flex items-center justify-center">
                                                    <Building2 className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                                                </div>
                                                <div className="ml-4">
                                                    <button
                                                        onClick={() => onEdit(team)}
                                                        className="text-left hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md p-1 -m-1 transition-colors"
                                                    >
                                                        <div className="text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:hover:text-brand-300 cursor-pointer">
                                                            {team.team_name}
                                                        </div>
                                                        {team.team_description && (
                                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                {team.team_description}
                                                            </div>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    {team.members && team.members.length > 0 ? (
                                                        <div className="space-y-2">
                                                            <div className="flex items-center">
                                                                <Users className="w-4 h-4 text-gray-400 mr-2" />
                                                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                                    {team.members.length} member{team.members.length !== 1 ? 's' : ''}
                                                                </span>
                                                            </div>
                                                            <div className="flex flex-wrap gap-1">
                                                                {team.members.slice(0, 3).map((member) => (
                                                                    <span
                                                                        key={member.uuid}
                                                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-100 text-brand-800 dark:bg-brand-900/20 dark:text-brand-400"
                                                                    >
                                                                        {member.userName}
                                                                    </span>
                                                                ))}
                                                                {team.members.length > 3 && (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                                                        +{team.members.length - 3} more
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center">
                                                            <Users className="w-4 h-4 text-gray-400 mr-2" />
                                                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                                                No members assigned
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center space-x-2 ml-4">
                                                    <button
                                                        onClick={() => setManagingTeam(team)}
                                                        className="p-1 hover:bg-brand-100 dark:hover:bg-brand-900/20 rounded-md transition-colors"
                                                        title="Manage team members"
                                                    >
                                                        <Settings className="w-4 h-4 text-brand-600 dark:text-brand-400" />
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
                <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                            Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                            {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                            {pagination.totalItems} teams
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => onPageChange?.(pagination.currentPage - 1)}
                                disabled={!pagination.hasPreviousPage || isLoading}
                                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                const pageNumber = i + 1;
                                return (
                                    <button
                                        key={pageNumber}
                                        onClick={() => onPageChange?.(pageNumber)}
                                        disabled={isLoading}
                                        className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                                            pageNumber === pagination.currentPage
                                                ? 'bg-brand-600 text-white'
                                                : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                        {pageNumber}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => onPageChange?.(pagination.currentPage + 1)}
                                disabled={!pagination.hasNextPage || isLoading}
                                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>

        {/* Team Members Management Modal */}
        {managingTeam && (
            <TeamMembersModal
                team={managingTeam}
                isOpen={!!managingTeam}
                onClose={() => setManagingTeam(null)}
                onUpdate={() => {
                    setManagingTeam(null);
                    onRefresh?.();
                }}
            />
        )}
    </>
    );
};

export default ModernTeamsTable;